import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TranslocoService } from '@ngneat/transloco';
import { HdBodygraphPlanetaryActivation } from '../hd-bodygraph.models';
import {
  HdCycleEncounterKind,
  HdCycleGateMainKeynoteEntry,
  HdCycleKeynotesBreakdown,
  buildHdCycleGateMainKeynoteIndex,
  getHdActivatedGates,
} from '../hd-cycle-keynotes.builder';
import {
  HD_CYCLE_ENCOUNTER_KIND_FA_ICONS,
  HD_CYCLE_MAIN_KEYNOTES,
  HD_CYCLE_PLANET_GROUPS,
} from '../hd-cycles-cheat-sheet-metadata';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HdPlanetInfoService } from '../hd-planet-info.service';
import { HdPlanetActivationBlockClickEvent } from '../planet-grid/hd-planet-activation-block/hd-planet-activation-block.component';
import {
  HD_CHIRON_DISPLAY_CONFIG,
  HD_PLANET_DISPLAY_CONFIGS,
  HdPlanetDisplayConfig,
  getHdPlanetDisplayConfigByPlanetId,
} from '../planet-grid/hd-planet-display-config';
import {
  HdJustNowBaseFixingPresentation,
  HdJustNowLineDataResolver,
  resolveHdJustNowBaseFixingPresentation,
} from '../planet-grid/hd-just-now-fixing.utils';
import { HdJustNowPlanetActivationCellComponent } from '../planet-grid/hd-just-now-planet-activation-cell/hd-just-now-planet-activation-cell.component';
import { HdSimpleExtraPlanetVm } from '../planet-grid/hd-simple-planet-grid/hd-simple-planet-grid.component';
import { resolveHdCycleOverlaySource } from '../hd-cycle-overlay.utils';
import { HdBodygraphChartPreset } from '../hd-bodygraph.models';

/** Eén main-keynote-chip in een flank (met de kanalen van die rol als tooltip). */
interface CycleGridMainKeynoteVm {
  readonly icon: string | null;
  readonly label: string;
  readonly color: string | null;
  readonly tooltip: string;
}

/** Flank-keynotes van één activatie (2 regels: mains + Tegenkoming/Vernieuwing, dan de groep). */
interface CycleGridAnnotationVm {
  /** Alle main-rollen van de poort (meestal 1; bij multi-kanaal-rollen meer). */
  readonly mains: readonly CycleGridMainKeynoteVm[];
  readonly encounterIcon: string;
  readonly encounterLabel: string;
  readonly encounterTooltip: string;
  readonly groupIcon: string | null;
  readonly groupLabel: string;
}

interface CycleGridRowVm {
  readonly displayConfig: HdPlanetDisplayConfig;
  readonly designActivation: HdBodygraphPlanetaryActivation | null;
  readonly personalityActivation: HdBodygraphPlanetaryActivation | null;
  readonly designFixing: HdJustNowBaseFixingPresentation | null;
  readonly personalityFixing: HdJustNowBaseFixingPresentation | null;
  readonly designAnnotation: CycleGridAnnotationVm | null;
  readonly personalityAnnotation: CycleGridAnnotationVm | null;
}

/**
 * De design/personality-planetengrid van het cycle-moment (zelfde cellen +
 * fixing t.o.v. natal als de grid in de chart-area), geflankeerd door de drie
 * cyclus-keynotes per activatie: links de design-kant, rechts de personality-
 * kant — per kant twee regels (Persoonlijk/Educatief/Omgevend + Tegenkoming/
 * Vernieuwing, daaronder Richting/Mensen/Ontwikkeling) mét de cheat-sheet-
 * iconen. Zon/aarde dragen geen planeet-groep; Chiron staat informatief
 * (gedimd) onderaan en doet niet mee in de keynotes.
 *
 * Op een momentchart (als cyclus behandeld, spec transits 2026-09-26 §2.8)
 * toont de grid enkel de personality-kolom met haar flank: een moment heeft
 * geen design-kant.
 */
@Component({
  selector: 'hd-cycle-planet-grid',
  templateUrl: './hd-cycle-planet-grid.component.html',
  styleUrl: './hd-cycle-planet-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [HdJustNowPlanetActivationCellComponent, NgTemplateOutlet],
})
export class HdCyclePlanetGridComponent {
  /** Preset van de actieve tab; alleen een cycle-preset rendert iets. */
  readonly preset = input.required<HdBodygraphChartPreset | null>();
  /** Breakdown uit de keynote-secties (voorkomt dubbel rekenwerk). */
  readonly breakdown = input.required<HdCycleKeynotesBreakdown | null>();

  readonly gateInfoRequested = output<{ gate: number; origin: { x: number; y: number } }>();

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly planetInfo = inject(HdPlanetInfoService);

  private readonly lang = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  private readonly overlay = computed(() => resolveHdCycleOverlaySource(this.preset()));

  /** Cycle-moment-activaties (gevalideerd, P+D; bij een moment enkel P). */
  private readonly cycleActivations = computed<readonly HdBodygraphPlanetaryActivation[]>(
    () => this.overlay()?.activations ?? [],
  );

  /** Momentchart: enkel de personality-kolom (geen design-kant). */
  protected readonly personalityOnly = computed(() => this.overlay()?.kind === 'moment');

  private readonly natalActivations = computed<readonly HdBodygraphPlanetaryActivation[]>(
    () => this.preset()?.planetaryActivations ?? [],
  );

  private readonly natalGates = computed(() => getHdActivatedGates(this.natalActivations()));

  private readonly mainKeynoteIndex = computed<ReadonlyMap<number, readonly HdCycleGateMainKeynoteEntry[]>>(() => {
    const breakdown = this.breakdown();
    return breakdown ? buildHdCycleGateMainKeynoteIndex(breakdown) : new Map();
  });

  protected readonly title = computed(() =>
    this.transloco.translate<string>(
      'humanDesign.chart.justNowTransit.planetGrid.baseTitle', undefined, this.lang(),
    ),
  );

  /** Zelfde lijn-fixing-resolver als de grids in de chart-area. */
  private readonly lineData: HdJustNowLineDataResolver = (gate, line) => {
    const translation = this.gateInfo.gateInfoIn(gate, this.lang()).translations.lines[line - 1];
    if (translation == null) {
      return null;
    }
    return {
      exaltationPlanet: translation.exaltation.planet,
      detrimentPlanet: translation.detriment.planet,
    };
  };

  protected readonly rows = computed<readonly CycleGridRowVm[]>(() => {
    const cycleActivations = this.cycleActivations();
    const natalActivations = this.natalActivations();
    return HD_PLANET_DISPLAY_CONFIGS.map(displayConfig => {
      const designActivation = cycleActivations.find(
        activation => activation.planetId === displayConfig.planetId && activation.side === 'design',
      ) ?? null;
      const personalityActivation = cycleActivations.find(
        activation => activation.planetId === displayConfig.planetId && activation.side === 'personality',
      ) ?? null;
      return {
        displayConfig,
        designActivation,
        personalityActivation,
        designFixing: designActivation
          ? resolveHdJustNowBaseFixingPresentation(designActivation, natalActivations, this.lineData)
          : null,
        personalityFixing: personalityActivation
          ? resolveHdJustNowBaseFixingPresentation(personalityActivation, natalActivations, this.lineData)
          : null,
        designAnnotation: designActivation ? this.annotationFor(designActivation) : null,
        personalityAnnotation: personalityActivation ? this.annotationFor(personalityActivation) : null,
      };
    });
  });

  /** Informatieve Chiron per zijde uit het cycle-moment-metadata (zoals de chart-area-grid). */
  protected readonly designChiron = computed(() => this.chironFromMetadata('design'));
  protected readonly personalityChiron = computed(() => this.chironFromMetadata('personality'));

  protected transitTooltip(presentation: HdJustNowBaseFixingPresentation | null): string {
    if (presentation == null || presentation.transitCauses.length === 0) {
      return '';
    }
    const lang = this.lang();
    const prefix = 'humanDesign.chart.justNowTransit.planetGrid';
    const translate = (key: string) => this.transloco.translate<string>(`${prefix}.${key}`, undefined, lang);
    return presentation.transitCauses.map(cause => {
      const fixingLabel = cause.fixing === 'exaltation' ? translate('exaltation') : translate('detriment');
      const relationLabel = cause.relation === 'same-gate' ? translate('sameGate') : translate('harmonicGate');
      return `${this.planetNameFor(cause.planetId)}: ${fixingLabel} (${relationLabel})`;
    }).join('\n');
  }

  protected onActivationClicked(event: HdPlanetActivationBlockClickEvent): void {
    this.gateInfoRequested.emit({ gate: event.activation.gate, origin: event.origin });
  }

  private planetNameFor(planetId: number): string {
    const config = getHdPlanetDisplayConfigByPlanetId(planetId);
    if (config == null) {
      return `${planetId}`;
    }
    return this.planetInfo.planetInfoIn(config.key, this.lang())?.name ?? config.key;
  }

  /** De drie keynotes van één cycle-activatie (mains kunnen ontbreken bij companionship). */
  private annotationFor(activation: HdBodygraphPlanetaryActivation): CycleGridAnnotationVm {
    const lang = this.lang();
    const entries = this.mainKeynoteIndex().get(activation.gate) ?? [];
    const encounter: HdCycleEncounterKind = this.natalGates().has(activation.gate) ? 'renewal' : 'encounter';
    const planetKey = getHdPlanetDisplayConfigByPlanetId(activation.planetId)?.key ?? null;
    const group = planetKey == null
      ? null
      : HD_CYCLE_PLANET_GROUPS.find(candidate => candidate.planets.includes(planetKey)) ?? null;
    return {
      mains: entries.map(entry => this.mainKeynoteVm(entry, lang)),
      encounterIcon: HD_CYCLE_ENCOUNTER_KIND_FA_ICONS[encounter],
      encounterLabel: this.cyclesText(`encounterRenewal.terms.${encounter}`, lang),
      encounterTooltip: this.cyclesText(`encounterRenewal.${encounter}`, lang),
      groupIcon: group?.faIcon ?? null,
      groupLabel: group == null ? '' : this.cyclesText(`planetGroups.${group.key}.title`, lang),
    };
  }

  /**
   * Chip per main-rol; de tooltip opent altijd met de "transit brengt: …"-regel
   * uit de sectiekop en belicht daaronder de kanalen waarin de poort die rol draagt.
   */
  private mainKeynoteVm(entry: HdCycleGateMainKeynoteEntry, lang: string): CycleGridMainKeynoteVm {
    const meta = HD_CYCLE_MAIN_KEYNOTES.find(candidate => candidate.key === entry.keynote) ?? null;
    const brings = this.cyclesText(`mainKeynotes.${entry.keynote}.brings`, lang).replace(/<[^>]*>/g, '');
    const bringsLine = `${this.cyclesText('bringsLabel', lang)} ${brings}`.trim();
    const channelLines = entry.channelKeys.map(channelKey => {
      const name = this.gateInfo.channelTranslationIn(channelKey, lang)?.name ?? '';
      return name ? `${channelKey} — ${name}` : channelKey;
    });
    return {
      icon: meta?.faIcon ?? null,
      label: this.cyclesText(`mainKeynotes.${entry.keynote}.title`, lang),
      color: meta?.color ?? null,
      tooltip: channelLines.length === 0 ? bringsLine : [bringsLine, '', ...channelLines].join('\n'),
    };
  }

  private chironFromMetadata(side: 'design' | 'personality'): HdSimpleExtraPlanetVm | null {
    const metadata = this.overlay()?.metadata;
    const extraPlanets = metadata?.['extraPlanets'];
    if (!Array.isArray(extraPlanets)) {
      return null;
    }
    const entry = (extraPlanets as Array<Record<string, unknown>>).find(
      candidate => candidate['planetId'] === HD_CHIRON_DISPLAY_CONFIG.planetId && candidate['side'] === side,
    );
    if (!entry) {
      return null;
    }
    return {
      glyph: HD_CHIRON_DISPLAY_CONFIG.astronomiconChar,
      gate: Number(entry['gate']),
      line: Number(entry['line']),
      tooltip: this.transloco.translate<string>(
        'humanDesign.chart.simpleGrid.chironTooltip', undefined, this.lang(),
      ),
    };
  }

  /** Tekst uit de cycles-scope met NL-fallback voor (nog) lege EN-velden. */
  private cyclesText(key: string, lang: string): string {
    const value = this.gateInfo.cyclesCheatSheetTextIn(key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cyclesCheatSheetTextIn(key, 'nl');
  }
}
